import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {
	Scene,
	WebGLRenderer,
	PerspectiveCamera,
	MeshBasicMaterial,
	Mesh,
	RingGeometry,
	DoubleSide,
	Raycaster,
	Vector2,
	TextureLoader,
	Sprite,
	SpriteMaterial,
} from 'three';
import {TextGeometry} from 'three/examples/jsm/geometries/TextGeometry.js';
import {FontLoader} from 'three/examples/jsm/loaders/FontLoader.js';

import store from '../../store';
import {setSelectedDay} from '../lightProfileSlice';
import {dayOfYear} from '../../util/dayOfYear';
import {temperatureToDecimal} from '../../util/temperatureToDecimal';
import {clearThreeObj} from '../../util/clearThreeObj';
import {lightProfiles} from '../lightProfiles';
import {selectProfileId, selectSpcConfiguration} from '../../selectors';
import {saveCanvasImage} from '../../slices/configurationSlice';

@customElement('light-profile-year')
export class LightProfileYear extends connect(store)(LitElement) {

	static styles = css`
		:host {
			display: flex;
		}

		canvas {
			height: auto !important;
			width: 100% !important;
			margin: auto;
		}

	`;

	segmentCount = 365;

	scene = new Scene();
	sceneBack = new Scene();
	sceneHighlight = new Scene();

	renderer = null;
	camera = new PerspectiveCamera(45, 1, 1, 500);
	raycaster = new Raycaster();
	pointer = new Vector2();
	innerRadius = 20;
	outerRadius = 38;
	year = 2024;
	font = null;
	gradientTexture = null;
	hoveredDay = null;
	hoveredSecond = null;
	connected = false;
	lightProfile = null;
	dotSprite = null;

	labelMaterial = new MeshBasicMaterial({
		color: 0x909090,
		side: DoubleSide,
		wireframe: false,
		opacity: 1,
		transparent: false
	});
	transparentMaterial = new MeshBasicMaterial({
		color: 0x000000,
		side: DoubleSide,
		wireframe: false,
		opacity: 0,
		transparent: true
	});
	backMaterial = new MeshBasicMaterial({
		color: 0x303030,
		side: DoubleSide,
		wireframe: false,
		opacity: 1,
		transparent: false
	});
	notSelectedMaterial = new MeshBasicMaterial({
		color: 0xff0000,
		side: DoubleSide,
		wireframe: false,
		opacity: 0,
		transparent: true
	});
	selectedMaterial = new MeshBasicMaterial({
		color: 0xff0000,
		side: DoubleSide,
		wireframe: false,
		opacity: 1,
		transparent: false
	});

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());

		this.renderer = new WebGLRenderer({alpha: true, antialias: true});
		this.renderer.setPixelRatio(window.devicePixelRatio);

		const sizeAttribute = this.getAttribute('size');
		const defaultSize = window.innerHeight - 320;
		const canvasSize = sizeAttribute ? parseInt(sizeAttribute, 10) : defaultSize;
		this.renderer.setSize(canvasSize, canvasSize);

		this.camera.position.set(0, 0, 100);
		this.camera.lookAt(0, 0, 0);
		this.camera.layers.enableAll();

		this.dotTexture = new TextureLoader().load('/line-assets/img/reddot.png');
		const dotMaterial = new SpriteMaterial({map: this.dotTexture});
		this.dotSprite = new Sprite(dotMaterial);
		this.dotSprite.position.set(0, 0, 0);
		this.dotSprite.scale.set(1, 1, 1);
		this.dotSprite.visible = false;
		this.sceneHighlight.add(this.dotSprite);
		this.loadFont();
	}

	setCanvasSize(width, height) {
		const canvas = this.renderer.domElement;
		canvas.style.width = `${width}px`;
		canvas.style.height = `${height}px`;
	}

	onCaptureImages() {
		this.captureCanvasImage();
	}
	captureCanvasImage() {
		this.threeRender();
		try {
			const dataUrl = this.renderer.domElement.toDataURL('image/png');
			//console.log('this.renderer.domElement:', this.renderer.domElement);
			//console.log('Capturing canvas image:', dataUrl);
			store.dispatch(saveCanvasImage({
				dataUrl: dataUrl,
				viewType: 'lightProfileYear'
			}));
		} catch (error) {
			console.error('Error capturing canvas image:', error);
		}
	}
	connectedCallback() {
		super.connectedCallback();
		this.connected = true;

		this.renderGraphics();

		this.animate();

		window.addEventListener('resize', () => this.onWindowResize());
		window.addEventListener('pointermove', (e) => this.onPointerMove(e));
		//window.addEventListener('captureImages', () => this.onCaptureImages());
		setTimeout(() => {
			this.captureCanvasImage();
		}, 500);

	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.connected = false;

		clearThreeObj(this.scene);
		this.scene.clear();
		clearThreeObj(this.sceneHighlight);
		this.sceneHighlight.clear();

		// this.renderer.renderLists.dispose();
		// this.renderer.dispose();

		window.removeEventListener('resize', () => this.onWindowResize());
		window.removeEventListener('pointermove', (e) => this.onPointerMove(e));
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.lightProfile = lightProfiles.find(profile => profile.id === selectProfileId(state));
		this.renderChannel = this.lightProfile?.renderChannels.find(rc => rc.id === state.lightProfile.data.channelId);
		if (this.renderChannel) {
			this.layers = this.renderChannel.layers;
			this.camera.layers.disableAll();
			this.camera.layers.enable(0);
			this.layers.forEach(layer => this.camera.layers.enable(layer + 1));
		} else {
			this.camera.layers.enableAll();
		}

		if (state.configuration.data.projects[0].project.dailySegments && state.configuration.data.projects[0].project.dailySegments.length > 0) {
			this.dailySegments = state.configuration.data.projects[0].project.dailySegments;
		} else {
			this.dailySegments = state.lightProfile.data.dailySegments;
		}

		if (this.selectedDay !== state.lightProfile.data.selectedDay || this.selectedSecond !== state.lightProfile.data.selectedSecond) {
			this.showSelectedDay(state.lightProfile.data.selectedDay, state.lightProfile.data.selectedSecond);
		}

		if (this.simulationIndex !== state.lightProfile.data.simulationIndex) {
			this.simulationIndex = state.lightProfile.data.simulationIndex;
			this.renderGraphics();
		}
	}

	loadFont() {
		const loader = new FontLoader();
		loader.load('/line-assets/three-fonts/droid/droid_sans_mono_regular.typeface.json', (response) => {
			this.font = response;
			this.renderGraphicsBack();
			this.loadTexture();
		});
	}

	loadTexture() {
		const loader = new TextureLoader();
		loader.load('/line-assets/alphagradient.png', (response) => {
			this.gradientTexture = response;
			this.renderGraphics();
		});
	}

	dayToAngle(day, offset = 0) {
		return Math.PI - (day / this.segmentCount + 1 / this.segmentCount * offset) * (Math.PI * 2);
	}

	paintDaySegmentBack(day) {
		const date = new Date(this.year, 0, day);
		const isFirstDayOfMonth = date.getUTCDate() === 1;
		const isSelected = day === this.selectedDay;


		const startAngle = this.dayToAngle(day, -0.15);
		const endAngle = this.dayToAngle(day, 0.15);
		const segment = new RingGeometry(this.innerRadius + (this.outerRadius - this.innerRadius) / 24, this.outerRadius, 3, 1, startAngle, endAngle - startAngle);
		const mesh = new Mesh(segment, this.backMaterial);
		const meshHighlight = new Mesh(segment, this.notSelectedMaterial);

		const startAngleHit = this.dayToAngle(day, -0.45);
		const endAngleHit = this.dayToAngle(day, 0.45);
		const segmentHit = new RingGeometry(this.innerRadius + (this.outerRadius - this.innerRadius) / 24, this.outerRadius, 3, 1, startAngleHit, endAngleHit - startAngleHit);
		const meshHit = new Mesh(segmentHit, this.transparentMaterial);

		mesh.layers.enable(0); // place on layer 0
		mesh.name = '' + day;

		meshHighlight.layers.enable(0);
		meshHighlight.name = '' + day + 'Highlight';

		meshHit.layers.enable(0);
		meshHit.name = '' + day + 'Hit';

		this.sceneBack.add(mesh);
		this.sceneBack.add(meshHit);
		this.sceneHighlight.add(meshHighlight);
	}

	paintMonthTick(month) {
		const firstDayDate = new Date(this.year, month, 1);
		const day = dayOfYear(firstDayDate);


		// labels for months
		if (this.font) {
			const monthName = firstDayDate.toLocaleString('en-us', {month: 'short'}).toUpperCase();
			const label = new TextGeometry(monthName, {
				font: this.font,
				size: 0.95,
				height: 0,
				depth: 0.1,
				curveSegments: 5,
				bevelEnabled: false
			});
			const startAngle = this.dayToAngle(day, 0);
			const labelMesh = new Mesh(label, this.labelMaterial);
			labelMesh.position.x = (this.outerRadius + 2) * Math.cos(startAngle) - 1;
			labelMesh.position.y = (this.outerRadius + 2) * Math.sin(startAngle) - 0.25;
			labelMesh.position.z = 0;
			labelMesh.layers.enable(0); // place on layer 1

			this.sceneBack.add(labelMesh);
			this.threeRender();
		}
	}


	paintDaySegments(day, segments, layer) {
		const segmentsForDay = segments.days[day];
		let layerColor = temperatureToDecimal(2700); // default Kelvin
		let layerDimming = 1;
		if (this.lightProfile && this.lightProfile.layers && this.lightProfile.layers[layer]) {
			layerColor = this.lightProfile.layers[layer].color;
			layerDimming = this.lightProfile.layers[layer].dimming;
		}
		if (segmentsForDay && segmentsForDay.length > 0) {
			segmentsForDay.forEach((segment, index) => {

				const startAngle = this.dayToAngle(day, -0.5);
				const endAngle = this.dayToAngle(day, 0.5);
				const hasFade = segment.brightness !== segment.prevBrightness;
				const fadeDirection = segment.brightness < segment.prevBrightness;
				const alpha = Math.max(segment.brightness, segment.prevBrightness); // (segment.brightness + segment.prevBrightness) / 2; // this.segmentSizeTemp * (0.3 + 0.7*(Math.sin(segment / this.segmentCount * Math.PI)));
				const segmentRadiusWidth = this.outerRadius - this.innerRadius;
				const segmentGeometry = new RingGeometry(this.outerRadius - segmentRadiusWidth * segment.start, this.outerRadius - segmentRadiusWidth * (segment.start + segment.size), 3, 1, startAngle, endAngle - startAngle);
				segmentGeometry.translate(0, 0, 0.1);
				let color = layerColor;
				if (segment.cct !== undefined) {
					color = temperatureToDecimal(segment.cct);
				}
				// console.log(segment.cct, layerColor, color);

				if (hasFade) {
					const offset = 1 - (segment.brightness - segment.prevBrightness);
					this.fadeUVS(segmentGeometry.attributes.uv, 1 - segment.prevBrightness, 1 - segment.brightness);
				}
				const material = new MeshBasicMaterial({
					color: color,
					side: DoubleSide,
					wireframe: false,
					opacity: alpha * layerDimming,
					transparent: true
				});
				if (hasFade) {
					material.opacity = 1;
					material.alphaMap = this.gradientTexture;
				}
				const mesh = new Mesh(segmentGeometry, material);
				mesh.layers.set(layer + 1);
				this.scene.add(mesh);
			});
		} else {
			// console.log('year: no  segment data for day ', day);
		}
	}

	fadeUVS(uvs, pin, pout) {
		uvs.setXY(0, pin, pin);
		uvs.setXY(1, pin, pin);
		uvs.setXY(2, pin, pin);
		uvs.setXY(3, pin, pin);

		uvs.setXY(4, pout, pout);
		uvs.setXY(5, pout, pout);
		uvs.setXY(6, pout, pout);
		uvs.setXY(7, pout, pout);

		uvs.setXY(8, pin, pin);
		uvs.setXY(9, pin, pin);
		uvs.setXY(10, pin, pin);
		uvs.setXY(11, pin, pin);

		uvs.setXY(12, pout, pout);
		uvs.setXY(13, pout, pout);
		uvs.setXY(14, pout, pout);
		uvs.setXY(15, pout, pout);
		uvs.needsUpdate = true;
	}

	showSelectedDay(day, second) {
		const dayRange = second / (24 * 60 * 60);
		const oldObject = this.sceneHighlight.getObjectByName('' + this.selectedDay + 'Highlight');
		const newObject = this.sceneHighlight.getObjectByName('' + day + 'Highlight');
		this.selectedDay = day;
		if (oldObject) oldObject.material = this.notSelectedMaterial;
		if (newObject) newObject.material = this.selectedMaterial;

		const angle = this.dayToAngle(day, 0);
		const x = (this.outerRadius - (this.outerRadius - this.innerRadius) * dayRange) * Math.cos(angle);
		const y = (this.outerRadius - (this.outerRadius - this.innerRadius) * dayRange) * Math.sin(angle);
		this.dotSprite.position.set(x, y, 0);
		this.dotSprite.visible = true;
		this.threeRender();
	}

	animate() {
		if (!this.connected) return;
		this.raycaster.setFromCamera(this.pointer, this.camera);
		this.raycaster.layers.set(0);
		const intersected = this.raycaster.intersectObjects(this.sceneBack.children);
		for (let i = 0; i < intersected.length; i++) {
			if (intersected[i].object.name.indexOf('Hit') !== -1) {
				// calculate distance from center point
				const distanceFronCenter = Math.sqrt(intersected[i].point.x * intersected[i].point.x + intersected[i].point.y * intersected[i].point.y);
				const relativeDistance = 1 - (distanceFronCenter - this.innerRadius) / (this.outerRadius - this.innerRadius);
				this.hoveredDay = parseInt(intersected[i].object.name, 10);
				this.hoveredSecond = Math.round(relativeDistance * 24 * 60 * 60 / (60 * 15)) * (60 * 15);
				if (this.hoveredDay !== this.state.lightProfile.data.selectedDay || this.hoveredSecond !== this.state.lightProfile.data.selectedSecond) {
					store.dispatch(setSelectedDay({
						selectedDay: this.hoveredDay,
						selectedSecond: this.hoveredSecond
					}));
				}
			}
		}
		//this.renderer.render(this.scene, this.camera);
		requestAnimationFrame(() => this.animate());
	}

	renderGraphicsBack() {
		for (let i = 0; i < 12; i++) {
			this.paintMonthTick(i);
		}
		for (let i = 0; i < this.segmentCount; i++) {
			this.paintDaySegmentBack(i);
		}
		this.threeRender();
	}

	renderGraphics() {
		if (!this.connected || !this.font || !this.gradientTexture) return;

		clearThreeObj(this.scene);
		this.scene.clear();
		if (this.dailySegments) {
			this.dailySegments.forEach((segments, index) => {
				for (let i = 0; i < this.segmentCount; i++) {
					this.paintDaySegments(i, segments, index);
				}
			});
		}
		this.threeRender();
	}

	onWindowResize() {

		const sizeAttribute = this.getAttribute('size');
		const defaultSize = window.innerHeight - 320;
		const canvasSize = sizeAttribute ? parseInt(sizeAttribute, 10) : defaultSize;

		// this.camera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(canvasSize, canvasSize);
		this.renderer.domElement.style.width = `${canvasSize}px`;
		this.renderer.domElement.style.height = `${canvasSize}px`;
		this.threeRender();
	}

	onPointerMove(event) {
		const rect = this.renderer.domElement.getBoundingClientRect();
		const width = rect.right - rect.left;
		const height = rect.bottom - rect.top;
		this.pointer.x = (event.clientX - rect.left - width / 2) / width * 2;
		this.pointer.y = -(event.clientY - rect.top - height / 2) / height * 2;
	}

	threeRender() {
		this.renderer.autoClear = true;
		this.renderer.render(this.sceneBack, this.camera);

		this.renderer.autoClear = false;
		this.renderer.clearDepth();
		this.renderer.render(this.scene, this.camera);

		this.renderer.autoClear = false;
		this.renderer.clearDepth();
		this.renderer.render(this.sceneHighlight, this.camera);
	}

	render() {
		const s = this.state;
		return html`
			${this.renderer.domElement}
		`;
	}
}
